import { Box, Typography, Paper, Grid, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Button, Tabs, Tab } from '@mui/material';
import type { ChipColor } from '@/types/chip';
import { School, Close, Event } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { QualificationRecord } from '../shared/types';
import type { TutoringAgreementRecord } from '@/types/entities';

interface TutorEmployeeLink {
  employeeNumber?: string;
  department?: string;
  jobTitle?: string;
  status?: string;
}

interface TutorRecordWithEmployee {
  id: string;
  legalEntity?: { type: string; id: string; firstName?: string; lastName?: string; email?: string; phone?: string };
  employmentRelationship: string;
  status: string;
  employee?: TutorEmployeeLink & { type: string; id: string };
}

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
  Suspended: 'error',
};

export const TutorDetail = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { data: qualifications } = useGetList('qualifications');
  const { data: agreements } = useGetList('tutoring-agreements');
  const { data: sessions, total: sessionCount } = useGetList('tutoring-sessions', { pagination: { page: 1, perPage: 1 } });
  const { data: schedules } = useGetList('tutoring-schedules');

    return (
      <Show render={({ record }) => {
        if (!record) return null;
        const tutorRecord = record as unknown as TutorRecordWithEmployee;

        const tutorQualifications = (qualifications || []).filter((q: QualificationRecord) => q.tutor?.id === record.id).slice(0, 10);
        const tutorAgreements = (agreements || []).filter((a: TutoringAgreementRecord) => a.tutor?.id === record.id).slice(0, 10);
        const tutorSchedules = (schedules || []).filter((s: any) => s.tutor?.id === record.id).slice(0, 10);

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/tutors')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Tutor"
            description="A qualified individual who delivers tutoring services. Tutors may also be employees of the organisation."
            icon={<School />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {record.legalEntity?.firstName || record.legalEntity?.lastName
                    ? `${record.legalEntity?.firstName ?? ''} ${record.legalEntity?.lastName ?? ''}`.trim()
                    : 'Unnamed Tutor'}
                </Typography>
                {record.legalEntity?.email && (
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>{record.legalEntity.email}</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                  <Chip label={record.employmentRelationship} size="small" />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Tutor Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">First Name</Typography>
                    <Typography variant="body1">{tutorRecord.legalEntity?.firstName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Last Name</Typography>
                    <Typography variant="body1">{tutorRecord.legalEntity?.lastName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{tutorRecord.legalEntity?.email || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{tutorRecord.legalEntity?.phone || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Employment Relationship</Typography>
                    <Typography variant="body1">{tutorRecord.employmentRelationship}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body1">{tutorRecord.status}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Employee Link</Typography>
                {tutorRecord.employee ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Employee Number</Typography>
                      <Typography variant="body2">{tutorRecord.employee.employeeNumber || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Department</Typography>
                      <Typography variant="body2">{tutorRecord.employee.department || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Job Title</Typography>
                      <Typography variant="body2">{tutorRecord.employee.jobTitle || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Typography variant="body2">{tutorRecord.employee.status || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/hr/onboarding/${tutorRecord.employee!.id}`)}
                      >
                        View Employee
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">Not linked to an Employee</Typography>
                )}
              </Paper>
            </Grid>

          </Grid>

          <Paper elevation={0} sx={{ mt: 3 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Qualifications" />
              <Tab label="Tutoring Agreements" />
              <Tab label="Schedules" />
              <Tab label="Sessions" />
            </Tabs>
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <School color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Qualifications</Typography>
                    <Chip label={`${tutorQualifications.length} records`} size="small" sx={{ ml: 1 }} />
                  </Box>
                  {tutorQualifications.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Issuer</TableCell>
                          <TableCell>Issued Date</TableCell>
                          <TableCell>Expiry Date</TableCell>
                          <TableCell>Verified</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tutorQualifications.map((q: QualificationRecord) => (
                          <TableRow key={q.id}>
                            <TableCell>{q.name}</TableCell>
                            <TableCell><Chip label={(q.qualificationType as any)?.typeName || q.qualificationType?.id || '—'} size="small" /></TableCell>
                            <TableCell>{q.issuer}</TableCell>
                            <TableCell>{q.issuedDate}</TableCell>
                            <TableCell>{q.expiryDate || '—'}</TableCell>
                            <TableCell>{q.verified ? 'Yes' : 'No'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No qualifications found</Typography>
                  )}
                </Box>
              )}
              {activeTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <School color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Tutoring Agreements</Typography>
                    <Chip label={`${tutorAgreements.length} records`} size="small" sx={{ ml: 1 }} />
                  </Box>
                  {tutorAgreements.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Client</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tutorAgreements.map((a: TutoringAgreementRecord) => (
                          <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tutoring-agreements/${a.id}/show`)}>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: 'underline', textUnderlineOffset: 2, color: 'primary.main' }}>
                                {a.clientName || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{a.effectiveFrom}</TableCell>
                            <TableCell>{a.effectiveTo || '—'}</TableCell>
                            <TableCell><Chip label={a.status} size="small" color={a.status === 'Active' ? 'success' : 'default'} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No tutoring agreements found</Typography>
                  )}
                </Box>
              )}
              {activeTab === 2 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Event color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Schedules</Typography>
                    <Chip label={`${tutorSchedules.length} records`} size="small" sx={{ ml: 1 }} />
                  </Box>
                  {tutorSchedules.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tutorSchedules.map((s: any) => (
                          <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tutoring-schedules/${s.id}/show`)}>
                            <TableCell>{s.subject || '—'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textDecoration: 'underline', textUnderlineOffset: 2, color: 'primary.main' }}>
                                {s.clientName || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell><Chip label={s.status} size="small" color={s.status === 'Active' ? 'success' : 'default'} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No schedules found</Typography>
                  )}
                </Box>
              )}
              {activeTab === 3 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Event color="primary" />
                      <Typography variant="subtitle1" fontWeight={700}>Sessions</Typography>
                      <Chip label={`${sessionCount ?? 0} total`} size="small" sx={{ ml: 1 }} />
                    </Box>
                    <Button size="small" onClick={() => navigate(`/tutoring-sessions?tutorId=${record.id}`)}>
                      View All
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }} />
  );
};
